// @noflow
import initI18n, { Language } from '@/packs/localisation'
import { makeVar, useReactiveVar } from '@apollo/client'
import * as Sentry from '@sentry/browser'
import i18next from 'i18next'
import React, { useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'

import { DiscountBasis } from '@/utils/butternutbox/discountCodes'

import BlogExitDiscountModal from '../components/shared/elements/BlogExitDiscountModal/BlogExitDiscountModal'
import { initCookieConsent } from '@/components/cookie_consent'
import TrustpilotWidget, {
  Theme,
  Variant
} from '@/components/elements/atoms/TrustpilotWidget/TrustpilotWidget'
import TransitionalModal from '@/components/shared/TransitionalModal'
import { Button } from '@/components/shared/elements/Button/Button'
import Cta from '@/components/shared/elements/RoughCostCalculator/components/CTA'
import DogDetailsForm from '@/components/shared/elements/RoughCostCalculator/components/DogDetailsForm'
import Plan from '@/components/shared/elements/RoughCostCalculator/components/Plan'

import type { Code as CountryCode } from '@/shared_types/rails_models/shipping_countries'

const dataContainer = document.getElementById('blog-content-tag') as HTMLElement

if (!dataContainer) {
  Sentry.captureException('Could not find #blog-content-tag')
}

const { dataset } = dataContainer
const {
  language,
  shippingCountryCode,
  discountBasis,
  discountFirstPartValue,
  trustpilotBusinessUnitId
} = dataset

const preferredLanguage = (language as Language) || 'en'
const countryCode = (shippingCountryCode as CountryCode) || 'GB'

const initLanguage = () => {
  if (!i18next.isInitialized) {
    initI18n(preferredLanguage)
  }
}

const showRCCModalState = makeVar({
  visible: false
})

const initDiscountModal = () => {
  const discountModalElement = document.getElementById('discount-modal')

  // The #discount-modal element is only rendered if the user is not logged in
  if (!discountModalElement) return

  const dataContainer = document.getElementById('discount-modal-content-tag')

  if (!dataContainer) {
    Sentry.captureException('Could not find #discount-modal-content-tag')
    return
  }

  const { dataset } = dataContainer
  const {
    discountCode,
    discountCopy,
    discountPercentage,
    funnelCompleted,
    shouldSeeBlogDiscountModalVariant
  } = dataset

  const shouldShowDiscountModal =
    discountCode &&
    discountCopy &&
    discountPercentage &&
    discountCode.length > 0

  const DiscountModalComponent = () => {
    const rccModal = useReactiveVar(showRCCModalState)

    return shouldShowDiscountModal && !rccModal.visible ? (
      <BlogExitDiscountModal
        discountCode={discountCode}
        discountCopy={discountCopy}
        discountPercentage={discountPercentage}
        funnelCompleted={funnelCompleted === 'true'}
        shouldSeeBlogDiscountModal={
          shouldSeeBlogDiscountModalVariant === 'true'
        }
      />
    ) : null
  }

  ReactDOM.render(<DiscountModalComponent />, discountModalElement)
}

const RoughCostCalculatorModal = (): JSX.Element => {
  const namespace = 'homepage'
  const copyContext = 'rough_cost_calculator'
  const baseClass = 'rough-cost-calculator__cost-calculator'

  const showRCCModal = useReactiveVar(showRCCModalState)

  const closeRCCModal = useCallback((): void => {
    showRCCModalState({ visible: false })
  }, [])

  const { t } = useTranslation(namespace)

  return (
    <TransitionalModal
      showCloseButton
      modalIsOpen={showRCCModal.visible}
      closeModal={closeRCCModal}
      modalSize="medium"
      transitionTypes={{ desktop: 'scale', mobile: 'bottom' }}
      extraClasses="rough-cost-calculator-modal__wrapper"
      withBackdrop={false}
    >
      <div className={`${baseClass}__blog`}>
        <div className={`${baseClass}__card`}>
          <h3 className={`display-20 no-scale ${baseClass}__blog__title`}>
            {t(`${copyContext}.alternative_title`)}
          </h3>
          <DogDetailsForm />
          <div className={`${baseClass}__plan-row-alternative`}>
            <Plan
              description={t(`${copyContext}.fresh_butternut`)}
              planType="bowl"
              copyContext={copyContext}
              namespace={namespace}
              preferredLanguage={preferredLanguage}
              discountBasis={discountBasis as DiscountBasis}
              discountValue={parseInt(discountFirstPartValue || '') || 0}
              shippingCountryCode={countryCode}
              align="center"
            />
          </div>
          <div className="rough-cost-calculator-modal__cta">
            <Cta
              namespace="blog"
              text="rough_cost_calculator_cta"
              screenIdentifier="blog"
            />
          </div>
          {trustpilotBusinessUnitId && (
            <TrustpilotWidget
              businessUnitId={trustpilotBusinessUnitId}
              variant={Variant.Default}
              theme={Theme.Light}
              height="80"
              width="100%"
            />
          )}
        </div>
      </div>
    </TransitionalModal>
  )
}

const initRoughCostCalculatorModal = (): void => {
  const roughCostCalculatorModalElement = document.getElementById(
    'rough-cost-calculator-modal'
  )
  if (!roughCostCalculatorModalElement) return

  ReactDOM.render(<RoughCostCalculatorModal />, roughCostCalculatorModalElement)
}

const StickyCTAComponent = (): JSX.Element | null => {
  const handleCTAClick = useCallback((): void => {
    showRCCModalState({ visible: true })
  }, [])

  return (
    <Button
      typography={{
        text: 'sticky_cta',
        namespace: 'blog'
      }}
      onClick={handleCTAClick}
      identifier="sticky_cta_rough_cost_calculator_modal"
      screenIdentifier="blog"
      fullWidth
    />
  )
}

const initStickyCTA = (): void => {
  const stickyCTA = document.getElementById('sticky-cta')
  if (!stickyCTA) return

  ReactDOM.render(<StickyCTAComponent />, stickyCTA)
}

const RoughCostCalculatorSidebar = (): JSX.Element | null => {
  const namespace = 'homepage'
  const copyContext = 'rough_cost_calculator'
  const baseClass = 'rough-cost-calculator__cost-calculator'

  const { t } = useTranslation(namespace)

  return (
    <div className={`${baseClass}__blog-sidebar`}>
      <div className={`${baseClass}__card ${baseClass}__blog-sidebar__card`}>
        <h3 className={`display-20 no-scale ${baseClass}__blog-sidebar__title`}>
          {t(`${copyContext}.alternative_title`)}
        </h3>
        <div className={`${baseClass}__plan-row-alternative`}>
          <Plan
            description={t(`${copyContext}.fresh_butternut`)}
            planType="bowl"
            copyContext={copyContext}
            namespace={namespace}
            preferredLanguage={preferredLanguage}
            discountBasis={discountBasis as DiscountBasis}
            discountValue={parseInt(discountFirstPartValue || '') || 0}
            shippingCountryCode={countryCode}
          />
        </div>
        <DogDetailsForm fullWidth />
        <div className="rough-cost-calculator-sidebar__cta">
          <Cta
            namespace="blog"
            text="rough_cost_calculator_cta"
            screenIdentifier="blog"
          />
        </div>
        {trustpilotBusinessUnitId && (
          <TrustpilotWidget
            businessUnitId={trustpilotBusinessUnitId}
            variant={Variant.Default}
            theme={Theme.Light}
            height="80"
            width="100%"
          />
        )}
      </div>
    </div>
  )
}

const initRoughCostCalculatorSidebar = (): void => {
  const roughCostCalculatorSidebarElement = document.getElementById(
    'rough-cost-calculator-sidebar'
  )
  if (!roughCostCalculatorSidebarElement) return

  ReactDOM.render(
    <RoughCostCalculatorSidebar />,
    roughCostCalculatorSidebarElement
  )
}

document.addEventListener('DOMContentLoaded', () => {
  initLanguage()
  initCookieConsent()
  initDiscountModal()
  initRoughCostCalculatorModal()
  initStickyCTA()

  initRoughCostCalculatorSidebar()
})
